import type {
  ActivityEvent,
  AddAnnotationInput,
  AgentConnection,
  Annotation,
  CaptureKind,
  CommandInput,
  CommandResult,
  Dictation,
  DownloadLink,
  EditorDocument,
  Folder,
  Invitation,
  InvitationLink,
  InvitationPreview,
  InviteMemberInput,
  Item,
  ItemPage,
  Member,
  NoteContent,
  Operation,
  PlaybackLink,
  Preferences,
  ReadAloud,
  Related,
  RevisionSummary,
  Role,
  SaveOutcome,
  SearchResult,
  Share,
  SiteVersion,
  SpeechVoice,
  TextSlice,
  TransformInput,
  UndoCommandInput,
  UndoOutcome,
  UpdatePreferencesInput,
  Upload,
  WorkspaceInfo,
  WorkspaceStatus,
} from '@mystiq/contracts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type TokenGetter = () => Promise<string | null>;
export type Me = { user: { id: string; email: string | null; displayName: string | null }; workspaces: Array<{ id: string; name: string; role: Role }> };
export type DevIdentity = { subject: string; displayName: string; description: string };

/** Signed transfer links come back absolute; the browser uses them through its own origin (dev proxy or same-host deploy). */
export const sameOrigin = (url: string) => {
  const parsed = new URL(url, window.location.origin);
  return `${parsed.pathname}${parsed.search}`;
};

async function sendBytes(url: string, body: Blob): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, { method: 'PUT', body, headers: { 'content-type': 'application/octet-stream' } });
  } catch {
    throw new ApiError(0, 'NETWORK', 'The connection was lost while sending the file. Check your connection and try again.');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => undefined);
    throw new ApiError(response.status, data?.error?.code ?? 'UNKNOWN', data?.error?.message ?? 'Upload failed.');
  }
}

/**
 * Sends a file to its upload link: in one request when it is one part,
 * otherwise part by part (needed above 100 MB when hosted), skipping the parts
 * the server already has, so sending again after a dropped connection carries
 * on where it stopped. A part whose connection drops is sent again after each
 * delay; a part the server refuses is not.
 */
async function sendUploadBytes(upload: Pick<Upload, 'uploadUrl' | 'partSize' | 'partCount'>, file: Blob, retryDelaysMs: number[]): Promise<void> {
  const url = sameOrigin(upload.uploadUrl);
  if (upload.partCount <= 1) return sendBytes(url, file);
  let received: number[] = [];
  try {
    const status = await fetch(url, { method: 'GET' });
    if (status.ok) received = ((await status.json()) as { receivedParts: number[] }).receivedParts;
  } catch {
    // Not knowing what arrived only means sending every part.
  }
  for (let part = 1; part <= upload.partCount; part++) {
    if (received.includes(part)) continue;
    const body = file.slice((part - 1) * upload.partSize, Math.min(part * upload.partSize, file.size));
    for (let attempt = 0; ; attempt++) {
      try {
        await sendBytes(`${url}/parts/${part}`, body);
        break;
      } catch (error) {
        const dropped = error instanceof ApiError && error.code === 'NETWORK';
        if (!dropped || attempt >= retryDelaysMs.length) throw error;
        await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
      }
    }
  }
}

export function createApi(getToken: TokenGetter) {
  /** acceptBody lets a caller treat specific non-2xx answers (e.g. a stale-save conflict) as results. */
  async function request<T>(method: string, path: string, body?: unknown, acceptBody?: (status: number, data: unknown) => boolean): Promise<T> {
    const token = await getToken();
    let response: Response;
    try {
      response = await fetch(path, {
        method,
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Could not reach Mystiq. Check your connection.');
    }
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (response.ok || acceptBody?.(response.status, data)) return data as T;
    throw new ApiError(response.status, data?.error?.code ?? 'UNKNOWN', data?.error?.message ?? response.statusText, data?.error?.details);
  }

  const w = (workspaceId: string) => `/api/workspaces/${workspaceId}`;
  // Only a 409 whose body is a save conflict is a result; other 409s (e.g. ITEM_UNAVAILABLE) are errors.
  const saveConflict = (status: number, data: unknown) => status === 409 && (data as { status?: string } | undefined)?.status === 'conflict';
  const query = (params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') search.set(key, String(value));
    const text = search.toString();
    return text ? `?${text}` : '';
  };

  return {
    me: () => request<Me>('GET', '/api/me'),
    devIdentities: () => request<DevIdentity[]>('GET', '/api/dev/identities'),

    workspace: (ws: string) => request<WorkspaceInfo>('GET', w(ws)),
    workspaceStatus: (ws: string) => request<WorkspaceStatus>('GET', `${w(ws)}/status`),
    agentSetup: (ws: string) => request<{ mcpUrl: string; oauthAvailable: boolean; connectionKeysAvailable: boolean }>('GET', `${w(ws)}/agent-setup`),
    members: (ws: string) => request<Member[]>('GET', `${w(ws)}/members`),
    changeRole: (ws: string, userId: string, role: Role) => request<Member>('PATCH', `${w(ws)}/members/${userId}`, { role }),
    removeMember: (ws: string, userId: string) => request<unknown>('DELETE', `${w(ws)}/members/${userId}`),
    invitations: (ws: string) => request<Invitation[]>('GET', `${w(ws)}/invitations`),
    invite: (ws: string, input: InviteMemberInput) => request<InvitationLink>('POST', `${w(ws)}/invitations`, input),
    revokeInvitation: (ws: string, invitationId: string) => request<Invitation>('POST', `${w(ws)}/invitations/${invitationId}/revoke`),
    previewInvitation: (token: string) => request<InvitationPreview>('POST', '/api/invitations/preview', { token }),
    acceptInvitation: (token: string) => request<WorkspaceInfo>('POST', '/api/invitations/accept', { token }),
    shares: (ws: string, itemId: string) => request<Share[]>('GET', `${w(ws)}/shares${query({ itemId })}`),
    createShare: (ws: string, itemId: string, options: { expiresInDays?: number; revisionId?: string } = {}) =>
      request<Share>('POST', `${w(ws)}/items/${itemId}/shares`, options),
    /** Points the link at the current version, or at a chosen one. */
    updateShare: (ws: string, shareId: string, revisionId?: string) => request<Share>('POST', `${w(ws)}/shares/${shareId}/update`, revisionId ? { revisionId } : {}),
    revokeShare: (ws: string, shareId: string) => request<Share>('POST', `${w(ws)}/shares/${shareId}/revoke`),

    folders: (ws: string) => request<Folder[]>('GET', `${w(ws)}/folders`),
    createFolder: (ws: string, name: string, parentId: string | null) => request<Folder>('POST', `${w(ws)}/folders`, { name, parentId }),
    renameFolder: (ws: string, folderId: string, name: string) => request<Folder>('PATCH', `${w(ws)}/folders/${folderId}`, { name }),
    moveFolder: (ws: string, folderId: string, parentId: string | null) => request<Folder>('PATCH', `${w(ws)}/folders/${folderId}`, { parentId }),
    deleteFolder: (ws: string, folderId: string) => request<unknown>('DELETE', `${w(ws)}/folders/${folderId}`),

    items: (ws: string, params: { folderId?: string; kind?: string; capture?: CaptureKind; view?: 'active' | 'trash'; cursor?: string; group?: boolean }) =>
      request<ItemPage>(
        'GET',
        `${w(ws)}/items${query({ folderId: params.folderId, kind: params.kind, capture: params.capture, view: params.view, cursor: params.cursor, group: params.group ? '1' : undefined })}`,
      ),
    item: (ws: string, itemId: string) => request<Item>('GET', `${w(ws)}/items/${itemId}`),
    updateItem: (ws: string, itemId: string, changes: { title?: string; description?: string | null; addTags?: string[]; removeTags?: string[] }) =>
      request<Item>('PATCH', `${w(ws)}/items/${itemId}`, changes),
    moveItem: (ws: string, itemId: string, folderId: string) => request<Item>('POST', `${w(ws)}/items/${itemId}/move`, { folderId }),
    trashItem: (ws: string, itemId: string) => request<Item>('POST', `${w(ws)}/items/${itemId}/trash`),
    restoreItem: (ws: string, itemId: string) => request<Item>('POST', `${w(ws)}/items/${itemId}/restore`),
    purgeItem: (ws: string, itemId: string) => request<Operation>('POST', `${w(ws)}/items/${itemId}/purge`),
    related: (ws: string, itemId: string, direction: 'sources' | 'derivatives') =>
      request<Related>('GET', `${w(ws)}/items/${itemId}/related${query({ direction })}`),
    readText: (ws: string, itemId: string, offset = 0) => request<TextSlice>('GET', `${w(ws)}/items/${itemId}/text${query({ offset, maxChars: 200000 })}`),
    editText: (ws: string, itemId: string, baseRevisionId: string, text: string, idempotencyKey: string) =>
      request<SaveOutcome>('PUT', `${w(ws)}/items/${itemId}/text`, { baseRevisionId, text, idempotencyKey }, saveConflict),
    itemOperations: (ws: string, itemId: string) => request<Operation[]>('GET', `${w(ws)}/items/${itemId}/operations`),
    downloadLink: (ws: string, itemId: string) => request<DownloadLink>('POST', `${w(ws)}/items/${itemId}/download-link`),
    playbackLink: (ws: string, itemId: string) => request<PlaybackLink>('POST', `${w(ws)}/items/${itemId}/playback-link`),
    readAloud: (ws: string, itemId: string, range?: { start: number; end: number }) => request<ReadAloud>('POST', `${w(ws)}/items/${itemId}/read-aloud`, range ?? {}),
    voices: (ws: string) => request<{ voices: SpeechVoice[] }>('GET', `${w(ws)}/voices`),
    preferences: (ws: string) => request<Preferences>('GET', `${w(ws)}/preferences`),
    updatePreferences: (ws: string, changes: UpdatePreferencesInput) => request<Preferences>('PATCH', `${w(ws)}/preferences`, changes),
    /** Sends a recorded clip and returns its words; the audio is not kept. */
    dictate: async (ws: string, clip: Blob): Promise<Dictation> => {
      const token = await getToken();
      let response: Response;
      try {
        response = await fetch(`${w(ws)}/dictation`, {
          method: 'POST',
          headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), 'content-type': clip.type || 'audio/webm' },
          body: clip,
        });
      } catch {
        throw new ApiError(0, 'NETWORK', 'Could not reach Mystiq. Check your connection.');
      }
      const data = await response.json().catch(() => undefined);
      if (!response.ok) throw new ApiError(response.status, data?.error?.code ?? 'UNKNOWN', data?.error?.message ?? response.statusText, data?.error?.details);
      return data as Dictation;
    },

    createNote: (ws: string, input: { folderId?: string; title?: string; markdown?: string }) => request<Item>('POST', `${w(ws)}/notes`, input),
    readNote: (ws: string, itemId: string) => request<NoteContent>('GET', `${w(ws)}/notes/${itemId}`),
    saveNote: (ws: string, itemId: string, baseRevisionId: string, document: EditorDocument, idempotencyKey: string) =>
      request<SaveOutcome>('PUT', `${w(ws)}/notes/${itemId}/content`, { baseRevisionId, document, idempotencyKey }, saveConflict),

    beginUpload: (
      ws: string,
      input: { folderId?: string; filename: string; title?: string; sizeBytes: number; mimeType?: string; sha256?: string; capture?: CaptureKind; idempotencyKey?: string },
    ) => request<Upload>('POST', `${w(ws)}/uploads`, input),
    putUploadBytes: (upload: Pick<Upload, 'uploadUrl' | 'partSize' | 'partCount'>, file: Blob, options: { retryDelaysMs?: number[] } = {}) =>
      sendUploadBytes(upload, file, options.retryDelaysMs ?? [1000, 3000]),
    completeUpload: (ws: string, uploadId: string) => request<Item>('POST', `${w(ws)}/uploads/${uploadId}/complete`),

    search: (ws: string, q: string) => request<SearchResult>('GET', `${w(ws)}/search${query({ q, limit: 30 })}`),
    retryOperation: (ws: string, operationId: string) => request<Operation>('POST', `${w(ws)}/operations/${operationId}/retry`),
    transform: (ws: string, input: TransformInput) => request<Operation>('POST', `${w(ws)}/transforms`, input),
    createSite: (
      ws: string,
      input: { sourceItemIds?: string[]; htmlFileItemId?: string; instructions?: string; title?: string; folderId?: string; idempotencyKey?: string },
    ) => request<Operation>('POST', `${w(ws)}/sites`, input),
    reviseSite: (ws: string, itemId: string, instructions: string, idempotencyKey: string) =>
      request<Operation>('POST', `${w(ws)}/items/${itemId}/revise`, { instructions, idempotencyKey }),
    /** A Site version's preview link (and, with maxChars, its HTML). */
    readSite: (ws: string, itemId: string, revisionId?: string, maxChars = 0) =>
      request<SiteVersion>('GET', `${w(ws)}/items/${itemId}/site${query({ revisionId, maxChars })}`),
    revisions: (ws: string, itemId: string) => request<RevisionSummary[]>('GET', `${w(ws)}/items/${itemId}/revisions`),
    annotations: (ws: string, itemId: string) => request<Annotation[]>('GET', `${w(ws)}/items/${itemId}/annotations`),
    addAnnotation: (ws: string, itemId: string, input: Omit<AddAnnotationInput, 'itemId'>) => request<Annotation>('POST', `${w(ws)}/items/${itemId}/annotations`, input),
    updateAnnotation: (ws: string, annotationId: string, changes: { body?: string; status?: 'open' | 'resolved' }) =>
      request<Annotation>('PATCH', `${w(ws)}/annotations/${annotationId}`, changes),
    deleteAnnotation: (ws: string, annotationId: string) => request<{ deleted: true }>('DELETE', `${w(ws)}/annotations/${annotationId}`),
    runCommand: (ws: string, input: CommandInput) => request<CommandResult>('POST', `${w(ws)}/commands`, input),
    undoCommand: (ws: string, input: UndoCommandInput) => request<UndoOutcome[]>('POST', `${w(ws)}/commands/undo`, input),
    reprocess: (ws: string, itemId: string, step: Operation['kind']) => request<Operation>('POST', `${w(ws)}/items/${itemId}/reprocess`, { step }),
    activity: (ws: string) => request<{ events: ActivityEvent[]; latestSeq: number }>('GET', `${w(ws)}/activity${query({ limit: 100 })}`),

    connections: (ws: string) => request<AgentConnection[]>('GET', `${w(ws)}/connections`),
    createConnectionKey: (ws: string, clientName: string) => request<{ connection: AgentConnection; key: string }>('POST', `${w(ws)}/connections/keys`, { clientName }),
    revokeConnection: (ws: string, connectionId: string) => request<AgentConnection>('POST', `${w(ws)}/connections/${connectionId}/revoke`),
    allowConnection: (ws: string, connectionId: string) => request<AgentConnection>('POST', `${w(ws)}/connections/${connectionId}/allow`),

    /**
     * Opens the change-hint stream; returns a function that closes it. A tab nobody is looking at holds none: each
     * connection asks the server, which then stays on, paid by the second. Back in view the page refetches anyway.
     */
    subscribe(ws: string, onChange: (seq: number) => void, onStatus: (connected: boolean) => void): () => void {
      let controller = new AbortController();
      let stopped = false;
      /** Set while waiting for the tab to come back into view. */
      let shown: (() => void) | undefined;
      const inView = () => new Promise<void>((resolve) => (document.hidden ? (shown = resolve) : resolve()));
      const onVisibility = () => {
        if (document.hidden) controller.abort();
        else shown?.();
      };
      document.addEventListener('visibilitychange', onVisibility);
      const loop = async () => {
        let delay = 1000;
        while (!stopped) {
          await inView();
          if (stopped) return;
          controller = new AbortController();
          try {
            const token = await getToken();
            const response = await fetch(`${w(ws)}/events`, { headers: token ? { authorization: `Bearer ${token}` } : {}, signal: controller.signal });
            if (!response.ok || !response.body) throw new Error(`events ${response.status}`);
            onStatus(true);
            delay = 1000;
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            for (;;) {
              const { value, done } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const frames = buffer.split('\n\n');
              buffer = frames.pop() ?? '';
              for (const frame of frames) {
                const event = /^event: (\w+)/m.exec(frame)?.[1];
                const data = /^data: (.*)$/m.exec(frame)?.[1];
                if ((event === 'change' || event === 'ready') && data) onChange((JSON.parse(data) as { seq: number }).seq);
              }
            }
          } catch {
            if (stopped) return;
          }
          // Let go because the tab was hidden: no connection was lost.
          if (controller.signal.aborted) continue;
          onStatus(false);
          await new Promise((resolve) => setTimeout(resolve, delay));
          delay = Math.min(delay * 2, 30_000);
        }
      };
      void loop();
      return () => {
        stopped = true;
        controller.abort();
        document.removeEventListener('visibilitychange', onVisibility);
        shown?.();
      };
    },
  };
}

export type Api = ReturnType<typeof createApi>;
